import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ImageIcon, X, Video } from 'lucide-react';
import { pb } from '@/lib/pocketbase';
import { toast } from 'sonner';
import { detectTextDirection } from '@/utils/textDirection';

interface EditInspirePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  postId: string;
  initialContent: string;
  initialImage?: string;
  initialVideo?: string;
  onSuccess: () => void;
}

export const EditInspirePostModal = ({ 
  isOpen, 
  onClose, 
  postId, 
  initialContent,
  initialImage,
  initialVideo,
  onSuccess 
}: EditInspirePostModalProps) => {
  const [content, setContent] = useState(initialContent);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string>('');
  const [existingImage, setExistingImage] = useState<string | undefined>(initialImage);
  const [existingVideo, setExistingVideo] = useState<string | undefined>(initialVideo);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setContent(initialContent);
    setExistingImage(initialImage);
    setExistingVideo(initialVideo);
    setSelectedImage(null);
    setSelectedVideo(null);
    setPreviewUrl('');
    setVideoPreviewUrl('');
  }, [initialContent, initialImage, initialVideo, isOpen]);

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }
      
      // Clear video when selecting image
      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
        setVideoPreviewUrl('');
        setSelectedVideo(null);
        setExistingVideo(undefined);
      }
      
      // Revoke old URL if exists
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      
      // Use FileReader for more reliable preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setPreviewUrl(result);
        setSelectedImage(file);
        setExistingImage(undefined);
        toast.success('Image loaded for preview');
      };
      reader.onerror = () => {
        toast.error('Failed to load image');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (file) {
      if (!file.type.startsWith('video/')) {
        toast.error('Please select a valid video file');
        return;
      }

      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        toast.error('Video file is too large. Maximum size is 100MB.');
        return;
      }
      
      // Clear image when selecting video
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl('');
        setSelectedImage(null);
        setExistingImage(undefined);
      }
      
      // Revoke old video URL if exists
      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
      }
      
      const videoUrl = URL.createObjectURL(file);
      setVideoPreviewUrl(videoUrl);
      setSelectedVideo(file);
      setExistingVideo(undefined);
      toast.success('Video loaded for preview');
    }
  };

  const handleRemoveNewImage = () => {
    setPreviewUrl('');
    setSelectedImage(null);
  };

  const handleRemoveNewVideo = () => {
    if (videoPreviewUrl) {
      URL.revokeObjectURL(videoPreviewUrl);
    }
    setVideoPreviewUrl('');
    setSelectedVideo(null);
  };

  const handleRemoveExistingImage = () => {
    setExistingImage(undefined);
  };

  const handleRemoveExistingVideo = () => {
    setExistingVideo(undefined);
  };

  const handleSubmit = async () => {
    if (!content.trim()) {
      toast.error('Please enter some content');
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('content', content.trim());

      // Handle media updates - can only have one (image OR video)
      if (selectedImage) {
        formData.append('image', selectedImage);
        // Clear video if image is selected
        if (initialVideo) formData.append('video', '');
      } else if (selectedVideo) {
        formData.append('video', selectedVideo);
        // Clear image if video is selected
        if (initialImage) formData.append('image', '');
      } else {
        // Handle removals
        if (!existingImage && initialImage) {
          formData.append('image', '');
        }
        if (!existingVideo && initialVideo) {
          formData.append('video', '');
        }
      }

      await pb.collection('inspire_posts').update(postId, formData);
      
      toast.success('Post updated successfully!');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error updating post:', error);
      
      let errorMessage = 'Failed to update post. ';
      if (error?.response?.message) {
        errorMessage += error.response.message;
      } else if (error?.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please check your connection and try again.';
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] w-[95vw] sm:w-full">
        <DialogHeader>
          <DialogTitle>Edit Post</DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-8rem)] pr-4">
          <div className="space-y-4">
            <Textarea
              placeholder="What knowledge would you like to share?"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="min-h-[150px] resize-none"
              dir="auto"
              style={{ unicodeBidi: 'embed' }}
            />

          {/* Existing Image Preview */}
          {existingImage && !selectedImage && !selectedVideo && (
            <div className="relative">
              <img 
                src={`${pb.baseUrl}/api/files/inspire_posts/${postId}/${existingImage}`}
                alt="Current post image" 
                className="max-h-64 w-full rounded-lg object-cover"
              />
              <Button
                variant="destructive"
                size="sm"
                onClick={handleRemoveExistingImage}
                className="absolute top-2 right-2"
              >
                <X className="h-4 w-4 mr-1" />
                Remove
              </Button>
            </div>
          )}

          {/* Existing Video Preview */}
          {existingVideo && !selectedImage && !selectedVideo && (
            <div className="relative">
              <video 
                src={pb.files.getURL({ id: postId, collectionId: 'inspire_posts', collectionName: 'inspire_posts' } as any, existingVideo)}
                className="max-h-64 w-full rounded-lg object-contain bg-black"
                controls
                playsInline
                preload="metadata"
              />
              <Button
                variant="destructive"
                size="sm"
                onClick={handleRemoveExistingVideo}
                className="absolute top-2 right-2"
              >
                <X className="h-4 w-4 mr-1" />
                Remove
              </Button>
            </div>
          )}

          {/* New Image Preview */}
          {previewUrl && selectedImage && (
            <div className="w-full space-y-2">
              <p className="text-sm font-medium">Selected Image:</p>
              <div className="relative border-2 border-primary/20 rounded-lg overflow-hidden bg-muted/5">
                <img 
                  src={previewUrl} 
                  alt="Preview"
                  style={{
                    width: '100%',
                    maxHeight: '256px',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleRemoveNewImage}
                  className="absolute top-2 right-2 z-10"
                  type="button"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{selectedImage.name}</p>
            </div>
          )}

          {/* New Video Preview */}
          {videoPreviewUrl && selectedVideo && (
            <div className="w-full space-y-2">
              <p className="text-sm font-medium">Selected Video:</p>
              <div className="relative border-2 border-primary/20 rounded-lg overflow-hidden bg-black">
                <video 
                  src={videoPreviewUrl}
                  className="w-full max-h-64 object-contain"
                  controls
                  playsInline
                  preload="metadata"
                />
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleRemoveNewVideo}
                  className="absolute top-2 right-2 z-10"
                  type="button"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{selectedVideo.name}</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex gap-2">
              <input
                type="file"
                id="edit-image-upload"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
              <Button 
                variant="ghost" 
                size="sm" 
                type="button"
                onClick={() => document.getElementById('edit-image-upload')?.click()}
                className="w-full sm:w-auto"
              >
                <ImageIcon className="h-5 w-5 mr-2" />
                {(existingImage || selectedImage) && !selectedVideo ? 'Change Photo' : 'Add Photo'}
              </Button>

              <input
                type="file"
                id="edit-video-upload"
                accept="video/*"
                onChange={handleVideoSelect}
                className="hidden"
              />
              <Button 
                variant="ghost" 
                size="sm" 
                type="button"
                onClick={() => document.getElementById('edit-video-upload')?.click()}
                className="w-full sm:w-auto"
              >
                <Video className="h-5 w-5 mr-2" />
                {(existingVideo || selectedVideo) && !selectedImage ? 'Change Video' : 'Add Video'}
              </Button>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button 
                variant="outline" 
                onClick={onClose}
                className="w-full sm:w-auto order-2 sm:order-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={isSubmitting || !content.trim()}
                className="w-full sm:w-auto order-1 sm:order-2"
              >
                {isSubmitting ? 'Updating...' : 'Update Post'}
              </Button>
            </div>
          </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

